import { useMemo } from "react";
import { useDeals } from "@/pages/deals/hooks";
import { usePeople, usePeopleInfinite } from "@/entities/people/hooks";
import { PersonData } from "@/entities/people/types";
import { ComputedFields } from "@/services/api/types";
import { MIN_CHARACTERS_FOR_SEARCH } from "@/services/store/BoundStore";
import { candidateFromPerson, RecipientCandidate } from "./recipients";

const SEARCH_PAGE_SIZE = 20;

const toCandidates = (people: (PersonData | undefined)[]) =>
  people.flatMap((person) => {
    const candidate = person ? candidateFromPerson(person) : undefined;
    return candidate ? [candidate] : [];
  });

// Everyone related to the deal, the ones a rep added by hand first.
export const useDealRecipientCandidates = (dealId: string, enabled = true) => {
  const {
    entities: [deal],
    isLoading: dealLoading
  } = useDeals(dealId && enabled ? [dealId] : [], {
    enabled: enabled && !!dealId,
    computed_fields: ComputedFields.ALL
  });

  const ids = useMemo(
    () => [
      ...new Set([
        ...(deal?.manuallyAddedPeople ?? []),
        ...(deal?.automaticallyAddedPeople ?? []),
        ...(deal?.allDealPeople ?? [])
      ])
    ],
    [
      deal?.manuallyAddedPeople,
      deal?.automaticallyAddedPeople,
      deal?.allDealPeople
    ]
  );

  const { entities: people, isLoading: peopleLoading } = usePeople(ids, {
    enabled: enabled && ids.length > 0
  });

  const candidates: RecipientCandidate[] = useMemo(() => {
    const byId = new Map(
      people.flatMap((person) => (person ? [[person.id, person] as const] : []))
    );
    return toCandidates(ids.map((id) => byId.get(id)));
  }, [ids, people]);

  return { candidates, isLoading: dealLoading || peopleLoading };
};

// People anywhere in the account whose name or email matches what was typed.
export const useRecipientSearch = (term: string) => {
  const trimmed = term.trim();
  const active = trimmed.length >= MIN_CHARACTERS_FOR_SEARCH;
  const { entities, isLoading } = usePeopleInfinite(
    undefined,
    active ? { term: trimmed } : undefined,
    undefined,
    SEARCH_PAGE_SIZE,
    "firstName",
    true,
    false,
    active
  );
  const results: RecipientCandidate[] = useMemo(
    () => (active ? toCandidates(entities) : []),
    [active, entities]
  );
  return { results, isLoading: active && isLoading };
};
